import { useMemo, useState } from "react";
import { ListingHeader } from "./listing/ListingHeader";
import { ListingFooter } from "./listing/ListingFooter";
import { MobileContactBar } from "./listing/MobileContactBar";
import { SponsorCard } from "./listing/SponsorCard";
import { AdvertiserInquiryDialog } from "./listing/AdvertiserInquiryDialog";
import { AdvertiserSignupDialog } from "./listing/AdvertiserSignupDialog";
import { ShowingRequestDialog } from "./listing/ShowingRequestDialog";
import { useListingData } from "./listing/useListingData";
import { useHashScroll } from "./listing/useHashScroll";
import { CATEGORY_LABELS } from "./listing/constants";

export default function PropertyWebsiteSponsors() {
  const { slug, preview, listing, error } = useListingData();
  const [inquireAd, setInquireAd] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [showingOpen, setShowingOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const grouped = useMemo(() => {
    if (!listing) return [];
    const sponsors = listing.sponsors || {};
    const byId = new Map();
    Object.values(sponsors).forEach((list) => (list || []).forEach((ad) => byId.set(ad.id, ad)));
    const byCategory = new Map();
    byId.forEach((ad) => {
      const cat = ad.category || "other";
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat).push(ad);
    });
    return Array.from(byCategory.entries()).sort((a, b) => (CATEGORY_LABELS[a[0]] || a[0]).localeCompare(CATEGORY_LABELS[b[0]] || b[0]));
  }, [listing]);

  useHashScroll(Boolean(listing));

  if (error) return <main data-testid="public-listing-error" className="min-h-screen bg-white grid place-items-center p-8 text-center"><h1 className="text-xl font-serif-display text-[#202833]">{error}</h1></main>;
  if (!listing) return <main data-testid="public-listing-loading" className="min-h-screen grid place-items-center bg-white font-mono text-sm text-[#667085]">Loading sponsors…</main>;

  const contact = listing.contact;
  const brandLabel = contact?.name || listing.full_address || listing.project_name;
  const showSponsors = listing.sponsors_enabled !== false;

  return (
    <main data-testid="public-property-sponsors" className="min-h-screen bg-white text-[#202833]">
      {preview && <div data-testid="public-listing-preview-banner" className="bg-[#C7A361] text-[#111820] text-center text-xs font-mono uppercase tracking-[0.14em] py-2">Admin preview — this is how the page will look to visitors</div>}
      <ListingHeader slug={slug} brandLabel={brandLabel} preview={preview} onRequestShowing={() => setShowingOpen(true)} onMenuOpenChange={setMobileMenuOpen} />

      <section className="bg-[#111820] text-white py-14 sm:py-20 text-center">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <h1 data-testid="public-sponsors-title" className="font-serif-display text-2xl sm:text-3xl">Local Services &amp; Trusted Partners</h1>
          <p className="mt-3 text-sm text-white/60">Businesses recommended by the listing agent to help with your next move near {listing.full_address || listing.project_name}.</p>
        </div>
      </section>

      <div className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
        {showSponsors && grouped.length > 0 ? (
          <div className="space-y-12">
            {grouped.map(([category, ads]) => (
              <div key={category}>
                <h2 data-testid={`public-sponsors-category-${category}`} className="font-serif-display text-lg sm:text-xl text-[#202833] mb-5">{CATEGORY_LABELS[category] || category}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {ads.map((ad) => <SponsorCard key={ad.id} advertiser={ad} onInquire={setInquireAd} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p data-testid="public-sponsors-empty" className="text-center text-sm text-[#667085] py-10">No local service partners have been added for this property yet.</p>
        )}
      </div>

      <section id="advertise" className="scroll-mt-24 bg-[#F8F7F4] border-y border-[#E7E4DE] py-14 sm:py-20">
        <div className="mx-auto max-w-xl px-4 sm:px-6 text-center">
          <h2 className="font-serif-display text-xl sm:text-2xl text-[#202833]">Advertise on This Property Website</h2>
          <p className="mt-3 text-sm text-[#667085]">Reach homebuyers actively viewing this listing. Local businesses can request a featured spot.</p>
          <button data-testid="public-sponsors-advertise-cta" onClick={() => setSignupOpen(true)} className="mt-6 rounded-full bg-[#C7A361] text-[#111820] text-sm font-semibold px-6 py-3 hover:brightness-95 transition">Get in Touch</button>
        </div>
      </section>

      <ListingFooter slug={slug} brandLabel={brandLabel} brokerage={contact?.company} preview={preview} />
      <MobileContactBar phone={contact?.phone} onRequestShowing={() => setShowingOpen(true)} hidden={showingOpen || signupOpen || Boolean(inquireAd) || mobileMenuOpen} />

      <ShowingRequestDialog open={showingOpen} onClose={() => setShowingOpen(false)} slug={slug} agentName={contact?.name} address={listing.full_address || listing.project_name} />
      <AdvertiserInquiryDialog advertiser={inquireAd} slug={slug} onClose={() => setInquireAd(null)} />
      <AdvertiserSignupDialog open={signupOpen} onClose={() => setSignupOpen(false)} slug={slug} />
    </main>
  );
}
